export type ScheduleFrequency =
  | 'Once'
  | 'Weekly'
  | 'Monthly'
  | 'Quarterly'
  | 'Custom'

/** Parses ISO (yyyy-mm-dd) or display (dd/mm/yyyy) dates into a local Date. */
export function parseScheduleDate(value?: string): Date | null {
  if (!value) return null
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value)
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]))
  return null
}

function addMonthsClamped(start: Date, months: number): Date {
  const target = new Date(start.getFullYear(), start.getMonth() + months, 1)
  const lastDay = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0,
  ).getDate()
  target.setDate(Math.min(start.getDate(), lastDay))
  return target
}

/**
 * Counts how many times a schedule executes between Start Date and End Date
 * (both inclusive). The first execution is on the Start Date.
 * Returns 0 when the range is incomplete or invalid.
 */
export function countScheduledExecutions(
  frequency: string,
  startDate?: string,
  endDate?: string,
  customDays?: string,
): number {
  const start = parseScheduleDate(startDate)
  if (!start) return 0
  if (frequency === 'Once') return 1
  const end = parseScheduleDate(endDate)
  if (!end || end < start) return 0

  if (frequency === 'Weekly' || frequency === 'Custom') {
    const step =
      frequency === 'Weekly'
        ? 7
        : customDays === 'Daily'
          ? 1
          : Number(customDays) || 0
    if (step <= 0) return 0
    const dayMs = 24 * 60 * 60 * 1000
    const spanDays = Math.round((end.getTime() - start.getTime()) / dayMs)
    return Math.floor(spanDays / step) + 1
  }

  const monthStep = frequency === 'Quarterly' ? 3 : 1
  let count = 0
  for (let i = 0; ; i += monthStep) {
    if (addMonthsClamped(start, i) > end) break
    count++
  }
  return count
}

export function calculateScheduleTotals(
  amountPerExecution: number,
  frequency: string,
  startDate?: string,
  endDate?: string,
  customDays?: string,
) {
  const executions = countScheduledExecutions(
    frequency,
    startDate,
    endDate,
    customDays,
  )
  return {
    executions,
    totalAmount: amountPerExecution * executions,
  }
}
